// ============================================
// FILE 7: /frontend/src/components/listings/SharesProgressBoard.jsx
// PURPOSE: Show funding progress with pie chart (replaces ChamaMatchBoard)
// CHANGES: Visual progress display for shares model
// ============================================

import { useState, useEffect } from 'react';
import { TrendingUp, Loader2, Users, AlertCircle, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { chamaAPI } from '../../api/chama.api';

export default function SharesProgressBoard({ listingId, onBuyShares }) {
  const [sharesInfo, setSharesInfo] = useState(null);
  const [interests, setInterests] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (listingId) {
      fetchData();
    }
  }, [listingId]);

  const fetchData = async () => {
    try {
      const [sharesResponse, interestsResponse] = await Promise.all([
        chamaAPI.getSharesInfo(listingId),
        chamaAPI.getChamaInterests(listingId)
      ]);
      setSharesInfo(sharesResponse);
      setInterests(interestsResponse);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-6 sm:py-8">
          <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!sharesInfo || !sharesInfo.listing.allowGroupBuy) {
    return null;
  }

  const { stats, listing } = sharesInfo;

  // Fully funded
  if (stats.isFunded) {
    return (
      <Card className="border-2 border-green-300 bg-gradient-to-br from-green-50 to-emerald-50">
        <CardHeader className="p-3 sm:p-4 md:p-6">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg md:text-xl">
            <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
            100% Funded!
          </CardTitle>
        </CardHeader>

        <CardContent className="p-3 sm:p-4 md:p-6 space-y-3 sm:space-y-4">
          <div className="text-center py-3 sm:py-4">
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-2 sm:mb-3">
              <TrendingUp className="w-8 h-8 sm:w-10 sm:h-10 text-green-600" />
            </div>
            <p className="text-xs sm:text-sm text-green-800 font-medium">
              All {listing.totalShares} shares have been purchased!
            </p>
            <p className="text-[10px] sm:text-xs text-green-700 mt-1">
              Full payments due within 90 days
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Expired
  if (stats.isExpired) {
    return (
      <Card className="border-2 border-red-300 bg-gradient-to-br from-red-50 to-pink-50">
        <CardHeader className="p-3 sm:p-4 md:p-6">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg md:text-xl">
            <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
            Funding Expired
          </CardTitle>
        </CardHeader>

        <CardContent className="p-3 sm:p-4 md:p-6 space-y-3 sm:space-y-4">
          <div className="text-center py-3 sm:py-4">
            <p className="text-xs sm:text-sm text-red-800">
              The funding period for this property has ended.
            </p>
            <p className="text-[10px] sm:text-xs text-red-700 mt-1">
              Deposits will be refunded within 7-14 business days.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Active funding
  return (
    <Card className="border-2 border-purple-300 bg-gradient-to-br from-purple-50 to-pink-50">
      <CardHeader className="p-3 sm:p-4 md:p-6">
        <CardTitle className="flex items-center gap-2 text-base sm:text-lg md:text-xl">
          <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
          Funding Progress
        </CardTitle>
      </CardHeader>

      <CardContent className="p-3 sm:p-4 md:p-6 space-y-3 sm:space-y-4">
        {/* Progress Circle - Simple representation */}
        <div className="relative w-32 h-32 sm:w-40 sm:h-40 mx-auto">
          <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
            {/* Background circle */}
            <circle
              cx="50"
              cy="50"
              r="40"
              fill="none"
              stroke="#e5e7eb"
              strokeWidth="8"
            />
            {/* Progress circle */}
            <circle
              cx="50"
              cy="50"
              r="40"
              fill="none"
              stroke="url(#gradient)"
              strokeWidth="8"
              strokeDasharray={`${stats.percentageFunded * 2.51327} 251.327`}
              strokeLinecap="round"
            />
            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#9333ea" />
                <stop offset="100%" stopColor="#ec4899" />
              </linearGradient>
            </defs>
          </svg>
          {/* Center text */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-xl sm:text-2xl md:text-3xl font-bold text-purple-900">
              {stats.percentageFunded}%
            </span>
            <span className="text-[10px] sm:text-xs text-purple-700">Funded</span>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-2 sm:gap-3">
          <div className="bg-white rounded-lg p-2 sm:p-3 border border-purple-200">
            <div className="flex items-center gap-1.5 mb-1">
              <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" />
              <span className="text-[10px] sm:text-xs text-muted-foreground">Sold</span>
            </div>
            <p className="text-base sm:text-lg md:text-xl font-bold text-purple-600">
              {listing.sharesSold}
            </p>
            <p className="text-[9px] sm:text-[10px] text-muted-foreground">
              of {listing.totalShares} shares
            </p>
          </div>

          <div className="bg-white rounded-lg p-2 sm:p-3 border border-purple-200">
            <div className="flex items-center gap-1.5 mb-1">
              <Users className="w-3 h-3 sm:w-4 sm:h-4 text-pink-600" />
              <span className="text-[10px] sm:text-xs text-muted-foreground">Available</span>
            </div>
            <p className="text-base sm:text-lg md:text-xl font-bold text-pink-600">
              {stats.sharesRemaining}
            </p>
            <p className="text-[9px] sm:text-[10px] text-muted-foreground">
              shares left
            </p>
          </div>
        </div>

        {/* Amount Progress */}
        <div className="space-y-2">
          <div className="flex justify-between text-xs sm:text-sm">
            <span className="text-muted-foreground">Amount Raised</span>
            <span className="font-semibold">
              KES {stats.amountRaised.toLocaleString()}
            </span>
          </div>
          <Progress value={stats.percentageFunded} className="h-2" />
          <div className="flex justify-between text-[10px] sm:text-xs text-muted-foreground">
            <span>KES 0</span>
            <span>KES {parseFloat(listing.price).toLocaleString()}</span>
          </div>
        </div>

        {/* Interest Stats */}
        {interests && interests.stats.totalInterests > 0 && (
          <div className="bg-white rounded-lg p-2 sm:p-3 border border-purple-200">
            <p className="text-xs sm:text-sm text-center text-purple-700 font-medium mb-1">
              {interests.stats.totalInterests} {interests.stats.totalInterests === 1 ? 'person has' : 'people have'} expressed interest
            </p>
            <p className="text-[10px] sm:text-xs text-center text-muted-foreground">
              {interests.stats.totalSharesRequested} shares requested
            </p>
          </div>
        )}

        {/* Deadline */}
        {listing.fundingDeadline && (
          <div className="bg-yellow-50 rounded-lg p-2 sm:p-3 border border-yellow-200 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs sm:text-sm font-semibold text-yellow-900">
                Funding Deadline
              </p>
              <p className="text-[10px] sm:text-xs text-yellow-800">
                {new Date(listing.fundingDeadline).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </p>
            </div>
          </div>
        )}

        {/* Call to Action */}
        <div className="bg-white rounded-lg p-2 sm:p-3 border border-purple-200">
          <p className="text-xs sm:text-sm text-center text-purple-700 font-medium mb-2">
            Be part of this investment opportunity.
          </p>
          <Button
            onClick={onBuyShares}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 h-8 sm:h-9 md:h-10 text-xs sm:text-sm"
          >
            <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
            Buy Shares Now
          </Button>
        </div>

        {/* Info Note */}
        <p className="text-[9px] sm:text-[10px] text-center text-muted-foreground italic">
          10% deposit required to reserve shares. Full payment due within 90 days of 100% funding.
        </p>
      </CardContent>
    </Card>
  );
}